import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { gql } from '../lib/api';
import {
  DELETE_STAFF_MUTATION,
  SHIFTS_QUERY,
  STAFF_QUERY,
  STORES_QUERY,
  UPSERT_STAFF_MUTATION,
} from '../graphql/queries';
import { useUserGroups } from '../hooks/useUserGroups';
import { LaborBudgetSection } from '../components/labor/LaborBudgetSection';

interface Store {
  id: string;
  name: string;
}

interface Staff {
  staffKey: string;
  displayName: string;
  staffCode: string | null;
  employmentType: string | null;
  hourlyWage: number | null;
  monthlySalary: number | null;
  notes: string | null;
}

interface Shift {
  storeId: string;
  date: string;
  staffKey: string;
  displayName: string;
  actualWorkMinutes: number;
}

const EMPLOYMENT_OPTIONS = ['役員', '正社員', 'アルバイト', 'スポット'];

const dateRangeForMonth = (yearMonth: string): { from: string; to: string } => {
  const [y, m] = yearMonth.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    from: `${yearMonth}-01`,
    to: `${yearMonth}-${String(lastDay).padStart(2, '0')}`,
  };
};

const currentYearMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

interface StaffMinutesPerStore {
  // staffKey → storeId → minutes
  [staffKey: string]: { [storeId: string]: number; total: number };
}

export const LaborPage = () => {
  const groups = useUserGroups();
  const isAdmin = groups.includes('admin');

  // URL クエリパラメータと同期（store は人件費は全社管理なので不要、月のみ）
  const [searchParams, setSearchParams] = useSearchParams();
  const ymParam = searchParams.get('ym');
  const initYearMonth = /^\d{4}-\d{2}$/.test(ymParam ?? '') ? (ymParam as string) : currentYearMonth();
  const [yearMonth, setYearMonth] = useState(initYearMonth);

  useEffect(() => {
    const next = new URLSearchParams();
    if (yearMonth) next.set('ym', yearMonth);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [yearMonth, searchParams, setSearchParams]);

  const qc = useQueryClient();

  const storesQ = useQuery({
    queryKey: ['stores'],
    queryFn: () => gql<{ stores: Store[] }>(STORES_QUERY),
  });
  const stores = storesQ.data?.stores ?? [];

  const staffQ = useQuery({
    queryKey: ['staff'],
    queryFn: () => gql<{ staff: Staff[] }>(STAFF_QUERY),
  });

  const { from, to } = dateRangeForMonth(yearMonth);
  const shiftQueries = useQueries({
    queries: stores.map((s) => ({
      queryKey: ['shifts', s.id, from, to],
      queryFn: () => gql<{ shifts: Shift[] }>(SHIFTS_QUERY, { storeId: s.id, from, to }),
      enabled: !!s.id,
    })),
  });

  const minutesByStaff: StaffMinutesPerStore = useMemo(() => {
    const map: StaffMinutesPerStore = {};
    shiftQueries.forEach((q) => {
      for (const s of q.data?.shifts ?? []) {
        if (!map[s.staffKey]) map[s.staffKey] = { total: 0 };
        map[s.staffKey][s.storeId] = (map[s.staffKey][s.storeId] ?? 0) + s.actualWorkMinutes;
        map[s.staffKey].total += s.actualWorkMinutes;
      }
    });
    return map;
  }, [shiftQueries]);

  const upsert = useMutation({
    mutationFn: (vars: {
      staffKey: string;
      displayName?: string | null;
      input: { employmentType?: string | null; hourlyWage?: number | null; monthlySalary?: number | null; notes?: string | null };
    }) => gql(UPSERT_STAFF_MUTATION, vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['staff'] });
    },
  });

  const remove = useMutation({
    mutationFn: (vars: { staffKey: string }) => gql(DELETE_STAFF_MUTATION, vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['staff'] });
    },
  });

  const [showAddForm, setShowAddForm] = useState(false);
  const [newStaff, setNewStaff] = useState({
    displayName: '',
    employmentType: '',
    hourlyWage: '',
    monthlySalary: '',
  });

  const computeStaffPay = (s: Staff): number => {
    const totalMinutes = minutesByStaff[s.staffKey]?.total ?? 0;
    if ((s.employmentType === '正社員' || s.employmentType === '役員') && s.monthlySalary != null) {
      return s.monthlySalary;
    }
    if (s.hourlyWage != null) {
      return Math.round((totalMinutes / 60) * s.hourlyWage);
    }
    return 0;
  };

  // 月給型の店舗別按分は、店舗勤務時間 / 全店舗合計勤務時間 × 月給
  const computeStorePay = (s: Staff, storeId: string): number => {
    const m = minutesByStaff[s.staffKey];
    if (!m) return 0;
    const storeMinutes = m[storeId] ?? 0;
    if (storeMinutes === 0) return 0;
    if ((s.employmentType === '正社員' || s.employmentType === '役員') && s.monthlySalary != null) {
      return Math.round(s.monthlySalary * (storeMinutes / m.total));
    }
    if (s.hourlyWage != null) {
      return Math.round((storeMinutes / 60) * s.hourlyWage);
    }
    return 0;
  };

  const staff = staffQ.data?.staff ?? [];
  const totalPay = staff.reduce((s, st) => s + computeStaffPay(st), 0);
  const totalMinutes = Object.values(minutesByStaff).reduce((s, m) => s + m.total, 0);

  if (!isAdmin) {
    return (
      <div style={card}>
        <h1 style={{ margin: 0, fontSize: '1.4rem' }}>スタッフ・人件費</h1>
        <p style={{ marginTop: '0.75rem', color: '#868e96' }}>
          このページは admin 権限のユーザーのみが閲覧・編集できます。
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={pageHeader}>
        <h1 style={pageTitle}>スタッフ・人件費（全社）</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            type="month"
            value={yearMonth}
            onChange={(e) => setYearMonth(e.target.value)}
            style={selectStyle}
          />
        </div>
      </div>

      <LaborBudgetSection stores={stores} />

      <section style={notice}>
        <strong>給与計算ルール（スタッフ・シフトベース）</strong>
        <div style={{ fontSize: '0.78rem', color: '#1864ab', marginTop: 4 }}>
          • <strong>役員・正社員</strong>: 月給固定（勤務時間に関わらず）<br />
          • <strong>アルバイト・スポット</strong>: 実労働時間 × 時給<br />
          • <strong>掛け持ちスタッフ</strong>: 月給型は全店舗の勤務時間比で**自動按分**、時給型は各店舗の実労働時間×時給<br />
          • Airシフト「シフトデータ」CSV を各店舗分アップロードすると勤務時間が自動取り込みされます<br />
          • <strong>※ 上部の「月額固定値」が登録されていれば、トップページの人件費 KPI はそちらを優先します</strong>
        </div>
      </section>

      <section style={kpiGrid}>
        <KpiCard label="登録スタッフ数" value={`${staff.length} 名`} />
        <KpiCard label="今月の総勤務（全店舗）" value={`${(totalMinutes / 60).toFixed(1)} h`} />
        <KpiCard label="今月の人件費（計算値）" value={`¥${totalPay.toLocaleString()}`} />
      </section>

      <section style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ ...sectionTitle, marginBottom: 0 }}>新規スタッフ追加</h2>
          <button type="button" onClick={() => setShowAddForm((v) => !v)} style={linkBtn}>
            {showAddForm ? '閉じる' : '＋ 追加フォームを開く'}
          </button>
        </div>
        {showAddForm && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', alignItems: 'end' }}>
            <label style={{ fontSize: '0.78rem' }}>
              表示名
              <input
                type="text"
                value={newStaff.displayName}
                onChange={(e) => setNewStaff({ ...newStaff, displayName: e.target.value })}
                placeholder="例: 山田 太郎"
                style={inlineInput}
              />
            </label>
            <label style={{ fontSize: '0.78rem' }}>
              雇用区分
              <select
                value={newStaff.employmentType}
                onChange={(e) => setNewStaff({ ...newStaff, employmentType: e.target.value })}
                style={inlineInput}
              >
                <option value="">（未設定）</option>
                {EMPLOYMENT_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: '0.78rem' }}>
              時給
              <input
                type="number"
                value={newStaff.hourlyWage}
                onChange={(e) => setNewStaff({ ...newStaff, hourlyWage: e.target.value })}
                placeholder="1100"
                style={inlineInput}
              />
            </label>
            <label style={{ fontSize: '0.78rem' }}>
              月給
              <input
                type="number"
                value={newStaff.monthlySalary}
                onChange={(e) => setNewStaff({ ...newStaff, monthlySalary: e.target.value })}
                placeholder="280000"
                style={inlineInput}
              />
            </label>
            <button
              type="button"
              disabled={!newStaff.displayName.trim() || upsert.isPending}
              onClick={() => {
                const name = newStaff.displayName.trim();
                upsert.mutate(
                  {
                    staffKey: name,
                    displayName: name,
                    input: {
                      employmentType: newStaff.employmentType || null,
                      hourlyWage: newStaff.hourlyWage ? Number(newStaff.hourlyWage) : null,
                      monthlySalary: newStaff.monthlySalary ? Number(newStaff.monthlySalary) : null,
                    },
                  },
                  {
                    onSuccess: () => {
                      setNewStaff({ displayName: '', employmentType: '', hourlyWage: '', monthlySalary: '' });
                      setShowAddForm(false);
                    },
                  },
                );
              }}
              style={{ ...primaryBtn, gridColumn: 'span 4', marginTop: '0.5rem' }}
            >
              {upsert.isPending ? '追加中…' : 'スタッフを追加'}
            </button>
          </div>
        )}
      </section>

      <section style={card}>
        <h2 style={sectionTitle}>スタッフ一覧（全社）</h2>
        {staffQ.isLoading ? (
          <p style={muted}>読込中…</p>
        ) : staff.length === 0 ? (
          <p style={muted}>
            まだスタッフが登録されていません。「CSV 取込」から各店舗の Airシフトのシフトデータ CSV をアップロードすると自動登録されます。
          </p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>表示名</th>
                <th style={th}>雇用区分</th>
                <th style={{ ...th, textAlign: 'right' }}>時給</th>
                <th style={{ ...th, textAlign: 'right' }}>月給</th>
                {stores.map((st) => (
                  <th key={st.id} style={{ ...th, textAlign: 'right' }}>
                    {st.name}
                    <div style={{ fontWeight: 'normal', color: '#868e96', fontSize: '0.7rem' }}>
                      勤務 / 按分人件費
                    </div>
                  </th>
                ))}
                <th style={{ ...th, textAlign: 'right' }}>合計人件費</th>
                <th style={th}>操作</th>
              </tr>
            </thead>
            <tbody>
              {staff
                .sort((a, b) => {
                  const am = minutesByStaff[a.staffKey]?.total ?? 0;
                  const bm = minutesByStaff[b.staffKey]?.total ?? 0;
                  return bm - am;
                })
                .map((s) => (
                  <StaffRow
                    key={s.staffKey}
                    staff={s}
                    stores={stores}
                    minutesByStore={minutesByStaff[s.staffKey] ?? { total: 0 }}
                    pay={computeStaffPay(s)}
                    storePay={(storeId) => computeStorePay(s, storeId)}
                    onSave={(input) => upsert.mutate({ staffKey: s.staffKey, input })}
                    onDelete={() => {
                      if (confirm(`「${s.displayName}」を削除しますか？\n（過去のシフトレコードは残ります。CSV 再取込で自動再登録されます）`)) {
                        remove.mutate({ staffKey: s.staffKey });
                      }
                    }}
                    saving={upsert.isPending || remove.isPending}
                  />
                ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

interface StaffRowProps {
  staff: Staff;
  stores: Store[];
  minutesByStore: { [storeId: string]: number; total: number };
  pay: number;
  storePay: (storeId: string) => number;
  saving: boolean;
  onSave: (input: { employmentType: string | null; hourlyWage: number | null; monthlySalary: number | null; notes: string | null }) => void;
  onDelete: () => void;
}

const StaffRow = ({ staff, stores, minutesByStore, pay, storePay, saving, onSave, onDelete }: StaffRowProps) => {
  const [editing, setEditing] = useState(false);
  const [employmentType, setEmploymentType] = useState(staff.employmentType ?? '');
  const [hourlyWage, setHourlyWage] = useState(staff.hourlyWage?.toString() ?? '');
  const [monthlySalary, setMonthlySalary] = useState(staff.monthlySalary?.toString() ?? '');
  const [notes, setNotes] = useState(staff.notes ?? '');

  if (!editing) {
    return (
      <tr>
        <td style={td}><strong>{staff.displayName}</strong></td>
        <td style={td}>{staff.employmentType ?? <span style={muted}>未設定</span>}</td>
        <td style={{ ...td, textAlign: 'right' }}>
          {staff.hourlyWage != null ? `¥${staff.hourlyWage.toLocaleString()}` : '-'}
        </td>
        <td style={{ ...td, textAlign: 'right' }}>
          {staff.monthlySalary != null ? `¥${staff.monthlySalary.toLocaleString()}` : '-'}
        </td>
        {stores.map((st) => {
          const minutes = minutesByStore[st.id] ?? 0;
          const hours = minutes / 60;
          const p = storePay(st.id);
          return (
            <td key={st.id} style={{ ...td, textAlign: 'right', fontSize: '0.8rem' }}>
              {hours > 0 ? (
                <>
                  <div style={{ color: '#6c757d' }}>{hours.toFixed(1)}h</div>
                  <div style={{ fontWeight: 600 }}>{p > 0 ? `¥${p.toLocaleString()}` : '-'}</div>
                </>
              ) : (
                '-'
              )}
            </td>
          );
        })}
        <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
          {pay > 0 ? `¥${pay.toLocaleString()}` : '-'}
        </td>
        <td style={td}>
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" onClick={() => setEditing(true)} style={linkBtn}>編集</button>
            <button type="button" onClick={onDelete} style={dangerBtn} disabled={saving}>削除</button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td style={td}><strong>{staff.displayName}</strong></td>
      <td style={td}>
        <select
          value={employmentType}
          onChange={(e) => setEmploymentType(e.target.value)}
          style={inlineInput}
        >
          <option value="">（未設定）</option>
          {EMPLOYMENT_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </td>
      <td style={{ ...td, textAlign: 'right' }}>
        <input
          type="number"
          value={hourlyWage}
          onChange={(e) => setHourlyWage(e.target.value)}
          placeholder="1100"
          style={{ ...inlineInput, width: 90, textAlign: 'right' }}
        />
      </td>
      <td style={{ ...td, textAlign: 'right' }}>
        <input
          type="number"
          value={monthlySalary}
          onChange={(e) => setMonthlySalary(e.target.value)}
          placeholder="280000"
          style={{ ...inlineInput, width: 110, textAlign: 'right' }}
        />
      </td>
      {stores.map((st) => (
        <td key={st.id} style={{ ...td, textAlign: 'right', fontSize: '0.75rem', color: '#868e96' }}>
          {((minutesByStore[st.id] ?? 0) / 60).toFixed(1)}h
        </td>
      ))}
      <td style={{ ...td, fontSize: '0.75rem' }}>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="備考"
          style={{ ...inlineInput, width: 100 }}
        />
      </td>
      <td style={td}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              onSave({
                employmentType: employmentType || null,
                hourlyWage: hourlyWage ? Number(hourlyWage) : null,
                monthlySalary: monthlySalary ? Number(monthlySalary) : null,
                notes: notes || null,
              });
              setEditing(false);
            }}
            style={primaryBtn}
          >
            保存
          </button>
          <button type="button" onClick={() => setEditing(false)} style={linkBtn}>取消</button>
        </div>
      </td>
    </tr>
  );
};

interface KpiCardProps { label: string; value: string }
const KpiCard = ({ label, value }: KpiCardProps) => (
  <div style={kpiCardStyle}>
    <div style={kpiLabel}>{label}</div>
    <div style={kpiValue}>{value}</div>
  </div>
);

const pageHeader: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
};
const pageTitle: React.CSSProperties = { margin: 0, fontSize: '1.4rem' };
const selectStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  background: '#fff',
};
const notice: React.CSSProperties = {
  background: '#e7f5ff',
  border: '1px solid #a5d8ff',
  color: '#1864ab',
  borderRadius: 8,
  padding: '0.7rem 0.9rem',
  marginBottom: '1rem',
  fontSize: '0.85rem',
};
const kpiGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '0.75rem',
  marginBottom: '1rem',
};
const kpiCardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '0.9rem 1rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
};
const kpiLabel: React.CSSProperties = { color: '#6c757d', fontSize: '0.8rem' };
const kpiValue: React.CSSProperties = { fontSize: '1.4rem', fontWeight: 700, marginTop: 4 };
const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '1rem 1.25rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
  marginBottom: '1rem',
};
const sectionTitle: React.CSSProperties = {
  fontSize: '0.95rem',
  marginTop: 0,
  marginBottom: '0.75rem',
  color: '#495057',
};
const muted: React.CSSProperties = { color: '#868e96', fontSize: '0.85rem' };
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #dee2e6',
  background: '#f8f9fa',
};
const td: React.CSSProperties = {
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #f1f3f5',
};
const inlineInput: React.CSSProperties = {
  padding: '0.25rem 0.4rem',
  borderRadius: 4,
  border: '1px solid #ced4da',
  fontSize: '0.8rem',
};
const primaryBtn: React.CSSProperties = {
  padding: '0.25rem 0.6rem',
  borderRadius: 4,
  border: '1px solid #aa0b56',
  background: '#aa0b56',
  color: '#fff',
  cursor: 'pointer',
  fontSize: '0.8rem',
};
const linkBtn: React.CSSProperties = {
  padding: '0.25rem 0.6rem',
  borderRadius: 4,
  border: '1px solid #dee2e6',
  background: '#fff',
  color: '#1864ab',
  cursor: 'pointer',
  fontSize: '0.8rem',
};
const dangerBtn: React.CSSProperties = {
  padding: '0.25rem 0.6rem',
  borderRadius: 4,
  border: '1px solid #ffc9c9',
  background: '#fff5f5',
  color: '#aa0b56',
  cursor: 'pointer',
  fontSize: '0.8rem',
};
